import JSCookie from 'js-cookie';

import { DeviceInfo } from '@sravni/koa-utils/lib/middlewares/device';
import { IPublicUser } from '@sravni/types/lib/auth';

import { IMaterial } from '../interfaces/news';
import { isProdBuild } from '../utils/environment';
import { isServer } from '../utils/isServer';

export interface IEvent {
  event: 'adEvent' | 'mainEvent' | 'pageViewGA' | 'frontendReady';
  title?: string;
  path?: string;
  eventCategory?: string;
  eventAction?: string;
  eventLabel?: string;
  eventValue?: number;
  author?: string;
  tags?: string;
  contentType?: string;
  specproekt?: string;
  publishedDate?: string;
  deviceType?: 'mobile' | 'desktop' | 'tablet';
  pixelOn?: boolean;
  position?: number | string;
  sortType?: string;
}

interface IEventSource
  extends Pick<IEvent, 'event' | 'eventValue' | 'title' | 'path' | 'deviceType' | 'pixelOn' | 'sortType' | 'position'> {
  eventCategory?: string | string[];
  eventAction?: string | string[];
  eventLabel?: string | string[];
}

const PAGE_VIEW_GA_STATE: { title: string; path: string } = {
  title: '',
  path: '',
};

const stringifyEventValue = (strings: undefined | string | string[]): string | undefined => {
  return Array.isArray(strings) ? strings.join('|') : strings;
};

interface IAnalyticsWindow extends Window {
  googletag: any;
  definedSlots: Record<string, any>;
  adventum_overGoogleAd: string | undefined;
  adventum_sendHasOffers: Function;
  dataLayer: IEvent[];
  sravniDataLayer: IEvent[];
}

declare const window: IAnalyticsWindow;

// Send data to Data Layer
export const pushGoogleEvent = (event: IEvent) => {
  if (!isServer) {
    if (isProdBuild) {
      // setup initial datalayer before add new event
      window.sravniDataLayer = window.sravniDataLayer || [];
      window.sravniDataLayer.push(event);
    } else {
      // tslint:disable-next-line:no-console
      console.log('pushEvent', event);
    }
  }
};

export const pushUserId = (user: IPublicUser) => {
  if (!isServer) {
    if (isProdBuild) {
      // setup initial datalayer before add new event
      window.sravniDataLayer = window.sravniDataLayer || [];
      window.dataLayer = window.dataLayer || [];
      // @ts-ignore
      window.sravniDataLayer.push({ SravniUserId: user?.sub });
      // @ts-ignore
      window.dataLayer.push({ SravniUserId: user?.sub });
    } else {
      // tslint:disable-next-line:no-console
      console.log('pushEvent', { SravniUserId: user?.sub });
    }
  }
};

export const pushArticleViewEvent = (article: IMaterial, path: string) => {
  const title = article.title.replace(/(<([^>]+)>)/gi, '');
  const pathForState = path.replace(/\//gi, '');
  if (PAGE_VIEW_GA_STATE.path === pathForState) {
    return;
  }

  Object.assign(PAGE_VIEW_GA_STATE, {
    title,
    path: pathForState,
  });

  pushGoogleEvent({
    event: 'pageViewGA',
    contentType: 'article',
    author: article.authorName,
    title,
    tags: stringifyEventValue(article.tags),
    path,
    publishedDate: article.publishDate,
  });
};

export const sendChangeRouteSPAEvent = ({ path, title }: { title: string; path: string }) => {
  const pathForState = path.replace(/\//gi, '');
  if (PAGE_VIEW_GA_STATE.path === pathForState) {
    return;
  }

  Object.assign(PAGE_VIEW_GA_STATE, {
    title,
    path: pathForState,
  });

  pushEvent({
    event: 'pageViewGA',
    title,
    path,
  });
};

export const sendSpaIsReadyEvent = (deviceInfo: DeviceInfo) => {
  const device = deviceInfo.phone ? 'mobile' : deviceInfo.tablet ? 'tablet' : 'desktop';
  pushEvent({
    event: 'frontendReady',
    deviceType: device,
    pixelOn: true,
  });
};

// Push events
export const pushEvent = (event: IEventSource) => {
  pushGoogleEvent({
    ...event,
    eventAction: stringifyEventValue(event.eventAction),
    eventLabel: stringifyEventValue(event.eventLabel),
    eventCategory: stringifyEventValue(event.eventCategory),
  });
};

export const makeMainGEvent = (event: Partial<IEventSource>): IEventSource => ({
  eventLabel: undefined,
  eventAction: undefined,
  eventValue: undefined,
  eventCategory: undefined,
  ...event,
  event: 'mainEvent',
});

export const makeAdGEvent = (event: Partial<IEventSource>): IEventSource => ({
  eventLabel: undefined,
  eventAction: undefined,
  eventValue: undefined,
  eventCategory: undefined,
  ...event,
  event: 'adEvent',
});

export const sentReadArticleEvent = (percent: string, isAuto?: boolean) => {
  pushGoogleEvent({
    event: 'mainEvent',
    eventCategory: 'Контент',
    eventAction: !isAuto ? 'Скроллинг' : 'Автоматический Скроллинг',
    eventLabel: `Просмотрено - ${percent}`,
    eventValue: undefined,
  });
};

export const sentExtraMaterialsEvent = (url: string, title) => {
  pushGoogleEvent({
    event: 'adEvent',
    eventCategory: 'Контент',
    eventAction: title,
    eventLabel: url,
    eventValue: undefined,
  });
};

export const sentClickPreviewEvent = (url: string) => {
  pushGoogleEvent({
    event: 'adEvent',
    eventCategory: 'Контент',
    eventAction: 'Переход из превью',
    eventLabel: url,
    eventValue: undefined,
  });
};

export const sendLeaveCommentEvent = () => {
  pushGoogleEvent({
    event: 'mainEvent',
    eventCategory: 'Контент',
    eventAction: 'Комментировать',
    eventLabel: 'undefined',
    eventValue: undefined,
  });
};

export const sendLikeEvent = (url: string) => {
  pushGoogleEvent({
    event: 'adEvent',
    eventCategory: 'Контент',
    eventAction: 'Нравится',
    eventLabel: url,
    eventValue: undefined,
  });
};

export const sendAuthEvent = () => {
  pushEvent(
    makeMainGEvent({
      eventCategory: 'Прочее',
      eventAction: 'Вход в ЛК',
      eventLabel: 'Авторизация по номеру',
    }),
  );
};

export const sendPaywallEvent = () => {
  pushEvent(
    makeMainGEvent({
      eventCategory: 'Контент',
      eventAction: 'Мягкий пейволл|Заполнение формы',
      eventLabel: 'Журнал',
    }),
  );
};

export const sendArticleClickEvent = (label: string, position: number | string, sortType?: string) => {
  pushEvent(
    makeMainGEvent({
      eventCategory: 'Контент',
      eventAction: 'Переход из превью',
      eventLabel: label,
      position,
      sortType,
    }),
  );
};

export const sendShareClickEvent = (name: string) => {
  pushEvent(
    makeMainGEvent({
      eventCategory: 'Контент',
      eventAction: 'Поделиться',
      eventLabel: name,
    }),
  );
};

export const sendViewCommentEvent = () => {
  pushEvent(
    makeMainGEvent({
      eventAction: 'Просмотр Комментария',
      eventCategory: 'Контент',
      eventValue: undefined,
    }),
  );
};

export const subscribeArticleButtonsOnGaEvents = (element: HTMLDivElement) => {
  const allBlock = element?.querySelectorAll('[data-event-action]');

  if (!allBlock || !allBlock.length) return;

  const handleSentGaEvent = (elem) => () => {
    const { eventCategory, eventAction, eventLabel, eventValue } = elem.dataset;

    pushGoogleEvent({
      event: 'mainEvent',
      eventCategory: eventCategory,
      eventAction: eventAction,
      eventLabel: eventLabel,
      eventValue: eventValue,
    });
  };

  Array.from(allBlock).forEach((elem) => {
    if (elem instanceof HTMLElement) {
      elem.addEventListener('click', handleSentGaEvent(elem));
    }
  });

  return () => {
    Array.from(allBlock).forEach((elem) => {
      if (elem instanceof HTMLElement) {
        elem.removeEventListener('click', handleSentGaEvent(elem));
      }
    });
  };
};

function parseStringToObject<ReturnType extends Record<string, unknown>>(
  str: string,
  separator = ';',
): Record<keyof ReturnType, string> {
  const segments = decodeURIComponent(str).split(separator);
  const res = {};

  for (let i = 0; i < segments.length; i++) {
    const [name, ...parts] = segments[i].trim().split('=');

    if (name) {
      res[name] = parts.join('=');
    }
  }

  return res as Record<keyof ReturnType, string>;
}

export enum UTM {
  Medium = 'UtmMedium',
  Source = 'UtmSource',
  Campaign = 'UtmCampaign',
  Content = 'UtmContent',
  Term = 'UtmTerm',
}

export enum UtmValue {
  Medium = 'utmcmd',
  Source = 'utmcsr',
  Campaign = 'utmccn',
  Content = 'utmcct',
  Term = 'utmctr',
}

export const getYandexClientId = () => {
  const id = JSCookie.get('_ym_uid');
  return id;
};

export const getUtmz = () => {
  const utm = JSCookie.get('__utmz');
  const utmValues = parseStringToObject(utm, '|');

  return {
    [UTM.Medium]: utmValues[UtmValue.Medium], // получаем из куки _utmz - канал продаж: cpc, organic, direct и прочее
    [UTM.Source]: utmValues[UtmValue.Source], // получаем из куки _utmz - источник: yandex, google
    [UTM.Campaign]: utmValues[UtmValue.Campaign], // получаем из куки _utmz - наименование рекламной кампании
    [UTM.Content]: utmValues[UtmValue.Content], // получаем из куки _utmz - рекламный контент, объявление
    [UTM.Term]: utmValues[UtmValue.Term], // получаем из куки _utmz - ключевое слово объявления
  };
};
